import React, { useMemo, useRef, useState, useEffect } from 'react'

import {
  ContainsEditableProps,
  useEditor,
  _div,
  _span,
  _heading,
  FieldType,
  parseSlateText
} from '@toasttab/sites-components'
import classnames from 'classnames'

import {
  BackgroundImage,
  PaddingEnum,
  Review,
  ThemeTypeEnum
} from 'src/apollo/sites'
import { useGoogleBusinessProfileReviewsQuery } from 'src/apollo/onlineOrdering'
import { isLevelAA } from 'src/shared/js/colorUtils'
import { t } from 'src/translations'
import {
  getHighRatedGoogleReviews,
  getRelativeTime,
  isPlaceholderReview,
  mapManualReviewToNormalized,
  NormalizedReview
} from 'src/shared/js/reviewUtils'

import Carousel from 'shared/components/common/carousel/Carousel'
import {
  getBackgroundColorOrImageModule,
  getSectionPaddingModule
} from 'shared/components/common/editor_context/editableUtils'
import { Stars } from '@toasttab/sites-components'

import { useThemeColorScheme } from 'public/components/default_template/meta/useTheme'
import Image from 'shared/components/common/Image'
import MoreButton from './MoreButton'
import { CommunityHighlightsModal } from './CommunityHighlightsModal'
import SkeletonReviewCard from './SkeletonReviewCard'
import { InfoIcon } from '@toasttab/buffet-pui-icons'
import { useOOClient } from 'src/shared/components/common/oo_client_provider/OOClientProvider'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'

const WHITE = '#FFFFFF'
const BLACK = '#000000'
const STAR_YELLOW = '#FFE606'
const HARD_BLUE = '#3B66CC'
const QUOTE_GRAY = '#757575'
const NUM_SKELETON_CARDS = 3

const getAccessibleColor = (
  backgroundColor: string,
  preferredColors: string[],
  fallbackColor: string
): string => {
  return (
    preferredColors.find((color) => isLevelAA(backgroundColor, color)) ||
    fallbackColor
  )
}

type ReviewCardProps = {
  review: NormalizedReview
  index: number
  fillColor: string
  textColor: string
  backgroundColor?: string | null
  reviewTextBackgroundColor: string
  theme?: ThemeTypeEnum | null
  isEditor: boolean
} & ContainsEditableProps

const ReviewCard = (props: ReviewCardProps) => {
  const isGoogleReview = props.review.source === 'google'
  const pathToAuthorName = `${props.editPath}.authorName`
  const pathToReviewStarRating = `${props.editPath}.rating`
  const [isExpanded, setIsExpanded] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  const reviewContent = parseSlateText(props.review.content)

  useEffect(() => {
    if (contentRef.current && reviewContent) {
      const isClamped =
        contentRef.current.scrollHeight > contentRef.current.clientHeight
      setIsTruncated(isClamped)
    }
  }, [reviewContent])

  const colorsStyle =
    props.theme === ThemeTypeEnum.Wide
      ? {
          backgroundColor: 'transparent',
          color: props.textColor
        }
      : {}

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  const handleFocus = () => {
    setIsEditing(true)
  }

  const handleBlur = () => {
    setIsEditing(false)
  }

  const secondaryTextColor = useMemo(
    () =>
      getAccessibleColor(
        props.reviewTextBackgroundColor,
        [QUOTE_GRAY, props.textColor, WHITE, BLACK],
        props.textColor
      ),
    [props.reviewTextBackgroundColor, props.textColor]
  )

  const googleTextColor = useMemo(
    () =>
      getAccessibleColor(
        props.reviewTextBackgroundColor,
        [HARD_BLUE, props.textColor, WHITE, BLACK],
        props.textColor
      ),
    [props.reviewTextBackgroundColor, props.textColor]
  )

  return (
    <div className={classnames('reviewCard')} style={colorsStyle}>
      <div className='reviewWrapper'>
        <blockquote className='reviewContent'>
          <div className='reviewTop'>
            <div className='reviewHeader'>
              <div className='starRating'>
                <Stars
                  fillColor={props.fillColor}
                  count={props.review.rating}
                  size={28}
                  editPath={pathToReviewStarRating}
                  editable={!isGoogleReview}
                />
              </div>
              <span
                className='quoteChar'
                aria-hidden='true'
                style={{ color: secondaryTextColor }}
              >
                &ldquo;
              </span>
            </div>
            <div className='quote'>
              <div
                ref={contentRef}
                className={!isExpanded && !isEditing ? 'quoteClamped' : ''}
                data-testid={
                  !isExpanded && !isEditing ? 'quote-clamped' : undefined
                }
              >
                <div onFocus={handleFocus} onBlur={handleBlur}>
                  <_span
                    html={props.review.content}
                    editPath={`${props.editPath}.content`}
                    style={{ color: secondaryTextColor }}
                    editable={!isGoogleReview}
                    hideToolbar={true}
                  />
                </div>
              </div>
              <MoreButton
                isTruncated={isTruncated}
                isExpanded={isExpanded}
                onClick={toggleExpanded}
                color={props.fillColor}
                isEditor={props.isEditor}
              />
            </div>
          </div>
          <div className='reviewBottom'>
            <div className='reviewBottomLeft'>
              {props.review.updateTime && (
                <span
                  className='relativeDate'
                  style={{ color: secondaryTextColor }}
                >
                  {getRelativeTime(props.review.updateTime)}
                </span>
              )}
              <div className='authorInfo'>
                {props.review.authorImg && (
                  <div className='authorAvatar'>
                    <img
                      src={props.review.authorImg}
                      alt={props.review.authorName}
                    />
                  </div>
                )}
                {!props.review.authorImg && (
                  <div className='authorAvatar authorPlaceholder'>
                    <span>
                      {parseSlateText(props.review.authorName)
                        .charAt(0)
                        .toUpperCase()}
                    </span>
                  </div>
                )}
                <div className='authorDetails'>
                  <_div
                    html={props.review.authorName}
                    editPath={pathToAuthorName}
                    style={{ color: props.textColor }}
                    editable={!isGoogleReview}
                    hideToolbar={true}
                  />
                </div>
              </div>
            </div>
            {props.review.source === 'google' && (
              <div className='sourceBadge'>
                <Image
                  className='googleLogo'
                  src='icons/google.svg'
                  alt={t('reviews.google')}
                />
                <div className='sourceText'>
                  <span
                    className='postedOn'
                    style={{ color: secondaryTextColor }}
                  >
                    {t('reviews.posted-on')}
                  </span>
                  <span
                    className='sourceName'
                    style={{ color: googleTextColor }}
                  >
                    {t('reviews.google')}
                  </span>
                </div>
              </div>
            )}
          </div>
        </blockquote>
      </div>
    </div>
  )
}

type ReviewsProps = {
  id: string
  header?: string | null
  reviews: Review[]
  fillColor?: string
  theme?: ThemeTypeEnum | null
  backgroundColor?: string | null
  backgroundImage?: BackgroundImage | null
  padding?: PaddingEnum | null
  useGoogleReviews?: boolean | null
} & ContainsEditableProps

const Reviews = ({ padding = PaddingEnum.None, ...props }: ReviewsProps) => {
  const { useEditableRef, isEditor } = useEditor()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const carouselRef = useRef<HTMLDivElement>(null)

  const useGoogleReviews = props.useGoogleReviews ?? false
  const { selectedLocation } = useRestaurant()
  const ooClient = useOOClient()
  const restaurantGuid = selectedLocation.externalId

  const {
    data: googleReviewsData,
    loading: googleReviewsLoading,
    error: googleReviewsError
  } = useGoogleBusinessProfileReviewsQuery({
    client: ooClient,
    variables: {
      restaurantGuid,
      pageSize: 50,
      pageToken: null,
      orderBy: 'updateTime desc'
    },
    skip: !useGoogleReviews || !restaurantGuid,
    fetchPolicy: 'cache-and-network',
    ssr: false
  })

  const displayedReviews = useMemo(() => {
    if (!useGoogleReviews) {
      // Never publish the seeded sample reviews. They remain visible in the
      // editor as a starting point, but on the live site we filter them out so
      // the lorem-ipsum placeholder text is never shown to guests.
      const manualReviews = isEditor
        ? props.reviews
        : props.reviews.filter((review) => !isPlaceholderReview(review))
      return manualReviews.map(mapManualReviewToNormalized)
    }

    // When data is loading or undefined (SSR state), indicate loading state by returning null
    if (googleReviewsLoading || !googleReviewsData) {
      return null
    }

    // If there is an error fetching google reviews, don't display anything
    if (
      googleReviewsError ||
      googleReviewsData.restaurantV2?.__typename !== 'Restaurant'
    ) {
      console.error('Error fetching Google reviews:', googleReviewsError)
      return []
    }

    return getHighRatedGoogleReviews(
      googleReviewsData.restaurantV2.googleBusinessProfileReviews?.reviews || []
    )
  }, [
    useGoogleReviews,
    googleReviewsData,
    googleReviewsLoading,
    googleReviewsError,
    props.reviews,
    isEditor
  ])
  const { editableRef } = useEditableRef<HTMLDivElement>({
    name: 'review section',
    displayName: t('reviews.review-section'),
    actions: ['move-up', 'move-down', 'duplicate', 'delete'],
    path: props.editPath,
    skipRef: carouselRef,
    schema: {
      fields: [
        {
          path: `${props.editPath}.theme`,
          displayName: 'Layout',
          type: FieldType.Enum,
          value: props.theme ?? null,
          defaultValue: null,
          enums: [
            { displayName: 'Compact', value: null },
            {
              displayName: 'Large',
              value: ThemeTypeEnum.Wide
            }
          ]
        },
        getSectionPaddingModule(props.editPath, padding),
        getBackgroundColorOrImageModule(
          props.editPath,
          props.backgroundColor,
          props.backgroundImage
        )
      ]
    }
  })

  const backgroundColor = props.backgroundColor || WHITE

  const { reviewTextBackgroundColor, outerBackgroundColor } =
    props.theme === ThemeTypeEnum.Wide
      ? {
          reviewTextBackgroundColor: backgroundColor,
          outerBackgroundColor: backgroundColor
        }
      : {
          reviewTextBackgroundColor: WHITE,
          outerBackgroundColor: backgroundColor
        }

  const getColorFromTheme = useThemeColorScheme()
  const themeTextColor = getColorFromTheme(
    (theme) => theme.colorScheme.text.default,
    WHITE
  )

  const starColor = useMemo(
    () =>
      getAccessibleColor(
        reviewTextBackgroundColor,
        [props.fillColor, STAR_YELLOW, WHITE, BLACK].filter(
          (color): color is string => color !== undefined
        ),
        WHITE
      ),
    [props.fillColor, reviewTextBackgroundColor]
  )

  const reviewTextColor = useMemo(
    () =>
      getAccessibleColor(
        reviewTextBackgroundColor,
        [themeTextColor, WHITE, BLACK],
        WHITE
      ),
    [reviewTextBackgroundColor, themeTextColor]
  )

  const outerTextColor = useMemo(
    () =>
      getAccessibleColor(
        outerBackgroundColor,
        [themeTextColor, WHITE, BLACK],
        WHITE
      ),
    [outerBackgroundColor, themeTextColor]
  )

  const reviewSectionStyle = {
    color: outerTextColor,
    backgroundColor: 'transparent'
  }

  if (displayedReviews !== null && !displayedReviews.length && isEditor) {
    return (
      <div
        ref={editableRef}
        className={classnames('reviewsSection', 'emptySection', props.theme)}
      ></div>
    )
  }

  // Don't render anything if we have an empty array (not loading) on public site
  if (displayedReviews !== null && !displayedReviews.length && !isEditor) {
    return null
  }

  const pathToReview = (idx: number) => `${props.editPath}.reviews[${idx}]`

  const isLoadingGoogleReviews = displayedReviews === null

  const handleInfoClick = () => {
    setIsModalOpen(true)
  }

  const carouselItems = isLoadingGoogleReviews
    ? Array.from({ length: NUM_SKELETON_CARDS }, (_, index) => (
        <div
          key={`skeleton-${index}`}
          className='reviewCardWrapper carouselItem'
        >
          {SkeletonReviewCard}
        </div>
      ))
    : displayedReviews.map((review, index) => {
        const reviewKey = review.id || `review-${index}`
        return (
          <div key={reviewKey} className='reviewCardWrapper carouselItem'>
            <ReviewCard
              key={reviewKey}
              editPath={pathToReview(index)}
              review={review}
              index={index}
              fillColor={starColor}
              textColor={reviewTextColor}
              reviewTextBackgroundColor={reviewTextBackgroundColor}
              theme={props.theme}
              backgroundColor={reviewTextBackgroundColor}
              isEditor={isEditor}
            />
          </div>
        )
      })

  const renderSectionHeader = () => {
    if (
      props.theme === ThemeTypeEnum.Wide ||
      (useGoogleReviews && googleReviewsError)
    ) {
      return null
    }

    if (useGoogleReviews) {
      return (
        <div className='sectionHeader'>
          <div>
            <_heading
              styleLevel={2}
              editable={false}
              editPath={`${props.editPath}.reviewHeader`}
              html={t('reviews.community-highlights')}
            />
            <div className='chSubtitle'>
              <span className='chSubtitleText'>
                {t('reviews.community-highlights-subtitle')}
              </span>
              <button
                className='chInfoButton'
                onClick={handleInfoClick}
                aria-label={t('reviews.community-highlights-info-button')}
                type='button'
              >
                <InfoIcon accessibility='semantic' />
              </button>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className='sectionHeader'>
        <_heading
          styleLevel={2}
          html={props.header || t('reviews.default-header')}
          editPath={`${props.editPath}.reviewHeader`}
        />
      </div>
    )
  }

  return (
    <>
      <CommunityHighlightsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
      <div
        ref={editableRef}
        className={classnames('reviewsSection', props.theme, {
          loadingReviews: isLoadingGoogleReviews
        })}
        data-testid='reviews-section'
        style={reviewSectionStyle}
        role={isLoadingGoogleReviews ? 'status' : undefined}
        aria-busy={isLoadingGoogleReviews}
        aria-live={isLoadingGoogleReviews ? 'polite' : undefined}
      >
        {renderSectionHeader()}
        <Carousel
          id={props.id}
          theme={props.theme}
          primaryColor={props.fillColor}
          whiteInactiveArrow={props.theme === ThemeTypeEnum.Wide}
          carouselRef={carouselRef}
          textColor={outerTextColor}
          ariaLabel={t('reviews.reviews')}
          itemsPerPage={props.theme === ThemeTypeEnum.Wide ? 1 : undefined}
          items={carouselItems}
        />
      </div>
    </>
  )
}

export default Reviews
